import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Divider,
  Chip,
  Paper,
  Avatar,
  Alert,
  LinearProgress,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  Stack,
  TextField,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import {
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  FileCopy as FileCopyIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  AccessTime as TimeIcon,
  Comment,
} from "@mui/icons-material";
import Swal from "sweetalert2";
const PatientSummary = ({ patientId, PatientINFO }) => {
  // Card visibility state
  const [cardVisibility, setCardVisibility] = useState({
    contact: true,
    medical: true,
    insurance: true,
    provider: true,
    financial: true,
    authorizations: true,
    appointments: true,
    documents: true,
    Comments: true,
    // alerts: true,
  });

  // Simple state for all data
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Individual data states
  const [insuranceList, setInsuranceList] = useState([]);
  const [priorAuth, setPriorAuth] = useState({});
  const [provider, setProvider] = useState({});
  const [referringProvider, setReferringProvider] = useState({});
  const [claimsList, setClaimsList] = useState([]);
  const [AuthList, setAuthList] = useState([]);
  const [appointmentsList, setAppointmentsList] = useState([]);
  const [payments, setPayments] = useState({});

  // UI states
  const [showOptions, setShowOptions] = useState(false);
  const [activeCards, setActiveCards] = useState(7);

  // Load saved preferences
  useEffect(() => {
    const savedVisibility = localStorage.getItem("cardVisibility");
    if (savedVisibility) {
      try {
        const parsedVisibility = JSON.parse(savedVisibility);
        setCardVisibility(parsedVisibility);

        // Count active cards
        const activeCount = Object.values(parsedVisibility).filter(
          (v) => v
        ).length;
        setActiveCards(activeCount);
      } catch (err) {
        console.log("Error loading saved preferences");
      }
    }
  }, []);

  // Fetch data from API
  const [isEditing, setIsEditing] = useState(false);
  const [commentText, setCommentText] = useState("");
  const handleEdit = () => setIsEditing(true);
    const token = localStorage.getItem("Token");
        const auth = `Bearer ${token}`
   const handleSaveComments = () => {
    const postData = {
      id: PatientINFO?.id,
      comments: commentText,
    };

    axios
      .post(`${process.env.REACT_APP_API_URL}/Patients/updatePatientComments`, postData,  {
            headers: { Authorization: auth },
          })
      .then((response) => {
        Swal.fire({
          icon: "success",
          title: "Notes saved successfully",
          timer: 2000,
          showConfirmButton: false,
          willClose: () => null,
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) {
              swalPopup.style.zIndex = 5000;
              const swalContent = swalPopup.querySelector(".swal2-popup");
              if (swalContent) swalContent.style.zIndex = 5001;
            }
          },
        });
        setIsEditing(false); // Exit editing mode after save
      })
      .catch((error) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: `Error: ${error.message}`,
          willOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) {
              swalPopup.style.zIndex = 5000;
              const swalContent = swalPopup.querySelector(".swal2-popup");
              if (swalContent) swalContent.style.zIndex = 5001;
            }
          },
        });
      });
  };
  useEffect(() => {
    const fetchPatientData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("Token");
        const auth = `Bearer ${token}`;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/Patients/GetSummary?patientId=${patientId}`,
          {
            headers: { Authorization: auth },
          }
        );

        if (response) {
          // Set each data piece individually
          setInsuranceList(response.insurance || []);
          setPriorAuth(response.priorAuth || {});
          setProvider(response.provider || {});
          setReferringProvider(response.referringProvider || {});
          setClaimsList(response.claims || []);
          setAuthList(response.authorizationSummary || []);
          setAppointmentsList(response.appointments || []);
          setPayments(response.payments || {});
          setCommentText(response.patientsComments.comments);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch data");
        console.error("Error fetching patient data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchPatientData();
    }
  }, [patientId]);

  // Update active cards count
  useEffect(() => {
    const count = Object.values(cardVisibility).filter((v) => v).length;
    setActiveCards(count);
  }, [cardVisibility]);

  // Toggle card visibility
  const handleCardToggle = (cardKey) => {
    setCardVisibility((prev) => ({
      ...prev,
      [cardKey]: !prev[cardKey],
    }));
  };

  // Control functions
  const handleShowAll = () => {
    const allVisible = Object.keys(cardVisibility).reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    setCardVisibility(allVisible);
  };

  const handleShowNone = () => {
    const noneVisible = Object.keys(cardVisibility).reduce((acc, key) => {
      acc[key] = false;
      return acc;
    }, {});
    setCardVisibility(noneVisible);
  };

  const savePreferences = () => {
    localStorage.setItem("cardVisibility", JSON.stringify(cardVisibility));
    Swal.fire({
      icon: "success",
      title: "Preferences Saved",
      text: "Your card visibility preferences have been saved successfully!",
      timer: 2000, // Auto close after 2 seconds
      showConfirmButton: false,
    });
    setShowOptions(!showOptions);
  };

  const primaryInsurance =
    insuranceList.find((ins) => ins.type === "primary") || insuranceList[0];
  const secondaryInsurance =
    insuranceList.find((ins) => ins.type === "secondary") || insuranceList[1];

  // Get procedure and diagnosis codes
  const procedureCodes = [
    priorAuth?.procedureCode1,
    priorAuth?.procedureCode2,
    priorAuth?.procedureCode3,
  ].filter((code) => code && code.trim() !== "");

  const diagnosisCodes = [
    priorAuth?.diagnosisCode1,
    priorAuth?.diagnosisCode2,
    priorAuth?.diagnosisCode3,
  ].filter((code) => code && code.trim() !== "");

  // Get next appointment
  const nextAppointment =
    appointmentsList.length > 0 ? appointmentsList[0] : null;

  // Get recent claims
  const recentClaims = claimsList.slice(0, 3).map((claim) => ({
    id: claim.id,
    name: `Claim ID #${claim.id}`,
    date: claim.createdDate
      ? new Date(claim.createdDate).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "N/A",
    amount: `$${claim.totalCharges || 0}`,
    statusColor: claim.statusColor,
  }));
  const recentAuths = AuthList.slice(0, 3).map((row) => ({
    name: `Auth ID #${row.authId}`,
    date: row.priorAuthSubmitted
      ? new Date(row.priorAuthSubmitted).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "N/A",
    cptCode: row.cptCode,
    statusColor: row.statusColor,
  }));
  // Card configuration
  const cardConfig = {
    contact: { title: "Patient Info", icon: <PersonIcon />, size: 3 },
    medical: { title: "Medical Codes", icon: <HospitalIcon />, size: 3 },
    Comments: { title: "Patient's Comments", icon: <Comment />, size: 3 },
    insurance: { title: "Insurance", icon: <CreditCardIcon />, size: 3 },
    provider: { title: "Provider Info", icon: <HospitalIcon />, size: 3 },
    authorizations: {
      title: "Auth/VOBs",
      icon: <CheckCircleIcon />,
      size: 4,
    },
    documents: { title: "Recent Claims", icon: <FileCopyIcon />, size: 4 },
    appointments: { title: "Appointments", icon: <CalendarIcon />, size: 4 },
    financial: { title: "Financial", icon: <ReceiptIcon />, size: 12 },
  };
  const alerts = [
    ...(nextAppointment
      ? [
          {
            type: "info",
            text: `Upcoming appointment: ${new Date(
              nextAppointment.appointmentDate
            ).toLocaleDateString()}`,
          },
        ]
      : []),
  ];

  // Render Card Components
  const ContactCard = () => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e0e0e0",
        boxShadow: 1,
      }}
    >
      <CardContent sx={{ p: 1 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ bgcolor: "#e3f2fd", p: 1, borderRadius: 1, mr: 1.5 }}>
            <PersonIcon sx={{ color: "#1976d2", fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            Patient Information
          </Typography>
        </Box>

        <Stack spacing={1}>
          {/* Patient Basic Info */}
          <Box>
            <Typography variant="body1" fontWeight={600}>
              {PatientINFO?.lastName}, {PatientINFO?.firstName}
            </Typography>

            <Box mt={0.5} display="flex" flexDirection="column" gap={0.3}>
              <Typography variant="body2" fontWeight="500">
                <b>Email:</b> {PatientINFO?.emailAddress || "-"}
              </Typography>

              <Typography variant="body2" fontWeight="500">
                <b>DOB:</b> {PatientINFO?.dob || "-"}
              </Typography>

              <Typography variant="body2" fontWeight="500">
                <b>Phone:</b> {PatientINFO?.cellPhone || "-"}
              </Typography>

              <Typography
                variant="body2"
                fontWeight="500"
                sx={{ maxWidth: 220 }}
                noWrap
                title={PatientINFO?.address || "-"}
              >
                <b>Address:</b> {PatientINFO?.address || "-"}
              </Typography>
            </Box>
          </Box>
        </Stack>
        <Divider />
        <Box sx={{ bgcolor: "#fafafa", mt: 1, p: 1 }}>
          <Typography
            variant="caption"
            fontStyle="italic"
            color="text.secondary"
            sx={{
              textAlign: "center",
              lineHeight: 1.1,
              letterSpacing: "0.01em",
            }}
          >
            Detailed patient information is available in the demographic section
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  const MedicalCard = () => (
    <Card
      sx={{
        height: "100%",
        borderRadius: 3,
        border: "1px solid #e0e0e0",
        boxShadow: 1,
      }}
    >
      <CardContent sx={{ p: 1 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ bgcolor: "#ede7f6", p: 1, borderRadius: 1, mr: 1.5 }}>
            <HospitalIcon sx={{ color: "#5e35b1", fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            Diagnosis/Procedure Codes
          </Typography>
        </Box>

        <Stack spacing={2}>
          {/* Diagnosis Codes */}
          {diagnosisCodes.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight={600}
                mb={1}
              >
                Diagnosis Codes
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {diagnosisCodes.map((code, index) => (
                  <Chip
                    key={index}
                    label={code}
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: "#e8eaf6",
                      color: "#3f51b5",
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Procedure Codes */}
          <Divider />

          {procedureCodes.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                color="secondary"
                fontWeight={600}
                mb={1}
              >
                Procedure Codes
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5}>
                {procedureCodes.map((code, index) => (
                  <Chip
                    key={index}
                    label={code}
                    size="small"
                    sx={{
                      fontSize: "0.75rem",
                      height: 28,
                      borderRadius: 1.5,
                      bgcolor: "#f3e5f5",
                      color: "#7b1fa2",
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* No Codes */}
          {diagnosisCodes.length === 0 && procedureCodes.length === 0 && (
            <Typography variant="body2" color="textSecondary">
              No medical codes available
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const InsuranceCard = () => (
    <Card sx={{ height: "100%", borderRadius: 2, border: "1px solid #e0e0e0" }}>
      <CardContent sx={{ p: 1 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <Box sx={{ bgcolor: "#e8f5e9", p: 1, borderRadius: 1, mr: 1.5 }}>
            <CreditCardIcon sx={{ color: "#2e7d32", fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            Insurance
          </Typography>
        </Box>

        <Stack spacing={1}>
          {primaryInsurance && (
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight={600}
                // mb={1}
              >
                Primary Insurance
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {primaryInsurance.planName || "N/A"}
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Member ID
                  </Typography>
                  <Typography variant="body2">
                    {primaryInsurance.memberId || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Payer ID
                  </Typography>
                  <Typography variant="body2">
                    {primaryInsurance.payerId || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {secondaryInsurance && (
            <>
              <Divider />
              <Box>
                <Typography
                  variant="subtitle2"
                  color="secondary"
                  fontWeight={600}
                  mb={1}
                >
                  Secondary Insurance
                </Typography>
                <Typography variant="body2" fontWeight="500">
                  {secondaryInsurance.planName || "N/A"}
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Member ID
                    </Typography>
                    <Typography variant="body2">
                      {secondaryInsurance.memberId || "N/A"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary">
                      Payer ID
                    </Typography>
                    <Typography variant="body2">
                      {secondaryInsurance.payerId || "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}

          {!primaryInsurance && !secondaryInsurance && (
            <Typography variant="caption" color="textSecondary">
              No insurance information available
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const ProviderCard = () => {
    const providerName =
      provider.firstName || provider.lastName
        ? `${provider.firstName || ""} ${provider.lastName || ""}`.trim()
        : "N/A";

    const referringProviderName =
      referringProvider.firstName || referringProvider.lastName
        ? `${referringProvider.firstName || ""} ${
            referringProvider.lastName || ""
          }`.trim()
        : "N/A";

    return (
      <Card
        sx={{ height: "100%", borderRadius: 2, border: "1px solid #e0e0e0" }}
      >
        <CardContent sx={{ p: 1 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box sx={{ bgcolor: "#fff3e0", p: 1, borderRadius: 1, mr: 1.5 }}>
              <HospitalIcon sx={{ color: "#f57c00", fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight="600">
              Provider Info
            </Typography>
          </Box>

          <Stack spacing={1}>
            <Box>
              <Typography
                variant="subtitle2"
                color="primary"
                fontWeight={600}
                mb={1}
              >
                Attending Provider
              </Typography>
              <Typography variant="body2" fontWeight="500">
                {providerName}
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    NPI
                  </Typography>
                  <Typography variant="body2">
                    {provider.nPI || "N/A"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Phone
                  </Typography>
                  <Typography variant="body2">
                    {provider.phoneNo || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {referringProviderName !== "N/A" && (
              <>
                <Divider />
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="secondary"
                    fontWeight={600}
                    mb={1}
                  >
                    Referring Provider
                  </Typography>
                  <Typography variant="body2" fontWeight="500">
                    {referringProviderName}
                  </Typography>
                  <Box display="flex" justifyContent="space-between">
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        NPI
                      </Typography>
                      <Typography variant="body2">
                        {referringProvider.npi || "N/A"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="textSecondary">
                        Phone
                      </Typography>
                      <Typography variant="body2">
                        {referringProvider.phone || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const FinancialCard = () => (
    <Card sx={{ borderRadius: 2, border: "1px solid #e0e0e0", boxShadow: 1 }}>
      <CardContent sx={{ p: 1 }}>
        <Grid container spacing={1} alignItems="center">
          {/* Total Paid */}
          <Grid item>
            <Box
              textAlign="center"
              sx={{
                bgcolor: "#e8f5e9",
                px: 3,
                py: 1,
                borderRadius: 2,
                minWidth: 100,
                boxShadow: 0.5,
              }}
            >
              <Typography
                variant="subtitle1"
                color="success.dark"
                fontWeight="600"
              >
                ${payments.totalPaid}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total Paid
              </Typography>
            </Box>
          </Grid>

          {/* Total Charges */}
          <Grid item>
            <Box
              textAlign="center"
              sx={{
                bgcolor: "#e0f7fa",
                px: 3,
                py: 1,
                borderRadius: 2,
                minWidth: 100,
                boxShadow: 0.5,
              }}
            >
              <Typography
                variant="subtitle1"
                color="info.dark"
                fontWeight="600"
              >
                ${payments.totalCharges}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Total Charges
              </Typography>
            </Box>
          </Grid>

          {/* Patient Balance at the end */}
          <Grid item xs>
            <Box
              textAlign="right"
              sx={{
                bgcolor: "#ffebee",
                px: 3,
                py: 1,
                borderRadius: 2,
                minWidth: 120,
                boxShadow: 0.5,
              }}
            >
              <Typography variant="subtitle1" color="error" fontWeight="600">
                ${payments.totalBalance}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Patient Balance
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  // const PatientCommentsCard = () => {

  //   return (
  //     <Card
  //       sx={{
  //         borderRadius: 4,
  //         border: "1px solid",
  //         borderColor: "error.light",
  //         background: "linear-gradient(145deg, rgba(255,235,238,0.9), rgba(255,255,255,1))",
  //         boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  //         transition: "all 0.3s ease",
  //         "&:hover": {
  //           boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
  //           transform: "translateY(-2px)",
  //         },
  //       }}
  //     >
  //       <CardContent sx={{ p: 2.5 }}>
  //         <Grid container spacing={2}>
  //           {/* Header */}
  //           <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between">
  //             <Box display="flex" alignItems="center" gap={1}>
  //               <Box
  //                 sx={{
  //                   width: 32,
  //                   height: 32,
  //                   borderRadius: "50%",
  //                   bgcolor: "error.main",
  //                   display: "flex",
  //                   alignItems: "center",
  //                   justifyContent: "center",
  //                 }}
  //               >
  //                 <Comment sx={{ color: "#fff", fontSize: 18 }} />
  //               </Box>

  //               <Typography variant="subtitle1" fontWeight={700} letterSpacing={0.3}>
  //                 Comments/Notes
  //               </Typography>
  //             </Box>

  //             {/* Edit / Save Button */}
  //             {isEditing ? (
  //               <Button variant="contained" color="error" size="small" onClick={handleSave}>
  //                 Save
  //               </Button>
  //             ) : (
  //               <Button variant="outlined" color="error" size="small" onClick={handleSave}>
  //                 Edit
  //               </Button>
  //             )}
  //           </Grid>

  //           <Grid item xs={12}>
  //             <Box
  //               sx={{
  //                 position: "relative",
  //                 bgcolor: "#fff",
  //                 borderRadius: 3,
  //                 p: 2,
  //                 border: "1px dashed",
  //                 borderColor: "error.light",
  //               }}
  //             >
  //               {/* Accent bar */}
  //               <Box
  //                 sx={{
  //                   position: "absolute",
  //                   left: 0,
  //                   top: 0,
  //                   height: "100%",
  //                   width: 6,
  //                   borderRadius: "3px 0 0 3px",
  //                   bgcolor: "error.main",
  //                 }}
  //               />

  //               {isEditing ? (
  //                 <TextField
  //                   fullWidth
  //                   multiline
  //                   minRows={3}
  //                   variant="outlined"
  //                   value={commentText}
  //                   onChange={(e) => setCommentText(e.target.value)}
  //                   sx={{ pl: 1, "& .MuiOutlinedInput-root": { border: "none" } }}
  //                 />
  //               ) : (
  //                 <Typography variant="body2" sx={{ pl: 2, color: "error.dark", fontWeight: 500, lineHeight: 1.6 }}>
  //                   {commentText || "No comments available for this patient."}
  //                 </Typography>
  //               )}
  //             </Box>
  //           </Grid>
  //         </Grid>
  //       </CardContent>
  //     </Card>
  //   );
  // };

  const getStatusChipStyle = (color) => ({
    bgcolor: color || "#9e9e9e",
    color: "#fff",
    height: 20,
    fontSize: "0.65rem",
    fontWeight: 600,
  });
  const getStatusLabel = (color) => {
    switch (color) {
      case "Red":
        return "New";
      case "Orange":
        return "Pending";
      case "Purple":
        return "Pending Further Review";
      case "Green":
        return "Approved";
      default:
        return "Status";
    }
  };

  const AuthorizationsCard = () => (
    <Card sx={{ height: "100%", borderRadius: 2, border: "1px solid #e0e0e0" }}>
      <CardContent sx={{ p: 1 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{ bgcolor: "#f1f8e9", p: 1, borderRadius: 1, mr: 1.5 }}>
            <CheckCircleIcon sx={{ color: "#689f38", fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            Recent Auth/VOBs
          </Typography>
        </Box>

        {/* Recent Authorizations */}
        <Stack spacing={0.75}>
          {recentAuths.length > 0 ? (
            recentAuths.map((auth, index) => (
              <Box
                key={index}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                px={0.75}
                py={0.75}
                sx={{
                  bgcolor: "#fafafa",
                  borderRadius: 1,
                  border: `1px solid ${auth.statusColor || "#e0e0e0"}`,
                }}
              >
                {/* Left */}
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {auth.name}
                  </Typography>

                  <Typography variant="caption" color="textSecondary">
                    {auth.date} | CPT: {auth.cptCode || "N/A"}
                  </Typography>
                </Box>

                {/* Status Color */}
                <Chip
                  label={getStatusLabel(auth.statusColor)}
                  size="small"
                  sx={getStatusChipStyle(auth.statusColor)}
                />
              </Box>
            ))
          ) : (
            <Typography variant="caption" color="textSecondary">
              No VOBs available
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const AppointmentsCard = () => {
    const formatDate = (dateString) => {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const formatTime = (timeString) => {
      if (!timeString) return "N/A";
      return new Date(timeString).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    return (
      <Card
        sx={{ height: "100%", borderRadius: 2, border: "1px solid #e0e0e0" }}
      >
        <CardContent sx={{ p: 1 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Box sx={{ bgcolor: "#e3f2fd", p: 1, borderRadius: 1, mr: 1.5 }}>
              <CalendarIcon sx={{ color: "#1976d2", fontSize: 20 }} />
            </Box>
            <Typography variant="subtitle1" fontWeight="600">
              Appointments
            </Typography>
          </Box>

          <Stack spacing={2}>
            {nextAppointment ? (
              <Box sx={{ bgcolor: "#e8f5e9", p: 1.5, borderRadius: 1.5 }}>
                <Typography
                  variant="caption"
                  color="primary"
                  fontWeight="600"
                  display="block"
                  mb={0.5}
                >
                  Next Appointment
                </Typography>
                <Typography variant="body1" fontWeight="600">
                  {formatDate(nextAppointment.appointmentDate)}
                </Typography>
                <Typography variant="body2">
                  {formatTime(nextAppointment.startTime)} •{" "}
                  {nextAppointment.appointmentReason || "Appointment"}
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  display="block"
                  mt={0.5}
                >
                  With: {nextAppointment.providerName || "Provider"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Duration: {nextAppointment.duration || 0} mins
                </Typography>
              </Box>
            ) : (
              <Box sx={{ bgcolor: "#f5f5f5", p: 1.5, borderRadius: 1.5 }}>
                <Typography variant="caption" color="textSecondary">
                  No upcoming appointments
                </Typography>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  };
  const getClaimStatus = (statusColor) => {
    switch (statusColor) {
      case "red":
        return { label: "New", bg: "red" };
      case "brown":
        return { label: "Secondary Pending", bg: "brown" };
      case "orange":
        return { label: "Pending", bg: "#ff9800" };
      case "purple":
        return { label: "Pending further review", bg: "purple" };
      case "Blueviolet":
        return { label: "Secondary Pending further review", bg: "Blueviolet" };
      case "green":
        return { label: "Completed", bg: "green" };
      case "black":
        return { label: "Closed", bg: "black" };
      case "skyblue":
        return { label: "Partially Paid", bg: "skyblue" };
      case "blue":
        return { label: "Paid", bg: "blue" };
      default:
        return { label: "Unknown", bg: "#9e9e9e" };
    }
  };

  const ClaimCard = () => (
    <Card sx={{ height: "100%", borderRadius: 2, border: "1px solid #e0e0e0" }}>
      <CardContent sx={{ p: 1 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={1}>
          <Box sx={{ bgcolor: "#f3e5f5", p: 1, borderRadius: 1, mr: 1.5 }}>
            <FileCopyIcon sx={{ color: "#7b1fa2", fontSize: 20 }} />
          </Box>
          <Typography variant="subtitle1" fontWeight="600">
            Recent Claims
          </Typography>
        </Box>

        {/* Rows */}
        <Stack spacing={0.75}>
          {recentClaims.length > 0 ? (
            recentClaims.map((doc) => {
              const status = getClaimStatus(doc.statusColor);

              return (
                <Box
                  key={doc.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  p={0.75}
                  sx={{
                    bgcolor: "#fafafa",
                    borderRadius: 1,
                    border: `1px solid ${status.bg}`,
                  }}
                >
                  {/* Left */}
                  <Box flex={1} minWidth={0}>
                    <Typography variant="body2" fontWeight={600}>
                      {doc.name}
                    </Typography>

                    <Box display="flex" gap={1}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        noWrap
                      >
                        {doc.date}
                      </Typography>

                      <Typography variant="caption" fontWeight={600}>
                        {doc.amount}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Status */}
                  <Chip
                    label={status.label}
                    size="small"
                    sx={{
                      bgcolor: status.bg,
                      color: "#fff",
                      fontWeight: 500,
                      height: "auto",
                      fontSize: "0.60rem",
                      maxWidth: 90,
                      "& .MuiChip-label": {
                        whiteSpace: "normal",
                        textAlign: "center",
                        lineHeight: 1.2,
                        padding: "2px 6px",
                      },
                    }}
                    noWrap
                  />
                </Box>
              );
            })
          ) : (
            <Typography variant="caption" color="textSecondary">
              No recent claims available
            </Typography>
          )}

          {/* {recentClaims.length > 0 && (
          <Typography
            variant="caption"
            color="primary"
            align="center"
            sx={{ mt: 0.5 }}
          >
            {recentClaims.length} recent claims
          </Typography>
        )} */}
        </Stack>
      </CardContent>
    </Card>
  );

  // Loading state
  if (loading) {
    return (
      <Container
        maxWidth="xl"
        sx={{
          py: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Box textAlign="center">
          <CircularProgress />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading patient summary...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Error state
  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading patient data: {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <React.Fragment>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 1,
          mb: 1,
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center">
            <Avatar
              sx={{
                width: 50,
                height: 50,
                mr: 2,
                bgcolor: "white",
                color: "#667eea",
              }}
            >
              {PatientINFO.lastName.toString().charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h5" fontWeight="bold" color={"#fff"}>
                {PatientINFO.lastName} {PatientINFO.firstName}
              </Typography>
              {/* <Box display="flex" gap={2} mt={0.5}>
                <Typography variant="caption">
                  Insurance: {insuranceList.length}
                </Typography>
                <Typography variant="caption">
                  Claims: {claimsList.length}
                </Typography>
                <Typography variant="caption">
                  Appointments: {appointmentsList.length}
                </Typography>
              </Box> */}
            </Box>
          </Box>

          <Tooltip title="Toggle Options">
            <IconButton
              onClick={() => setShowOptions(!showOptions)}
              sx={{ color: "white" }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Options Panel */}
        <Collapse in={showOptions}>
          <Box
            mt={2}
            pt={2}
            sx={{ borderTop: "1px solid rgba(255,255,255,0.3)" }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Typography variant="subtitle2" fontWeight="bold">
                SELECT CARDS TO DISPLAY ({activeCards}/9)
              </Typography>

              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleShowAll}
                  sx={{ color: "white", borderColor: "white" }}
                >
                  Show All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleShowNone}
                  sx={{ color: "white", borderColor: "white" }}
                >
                  Hide All
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={savePreferences}
                  sx={{ bgcolor: "white", color: "#667eea" }}
                >
                  Save
                </Button>
              </Box>
            </Box>

            {/* Checkboxes */}
            <Grid container spacing={1}>
              {Object.entries(cardConfig).map(([key, config]) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Box
                    onClick={() => handleCardToggle(key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      p: 1,
                      borderRadius: 1,
                      cursor: "pointer",
                      bgcolor: cardVisibility[key]
                        ? "rgba(255,255,255,0.15)"
                        : "transparent",
                      "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={cardVisibility[key]}
                      sx={{ color: "white", mr: 1 }}
                    />
                    <Box sx={{ color: "white", mr: 1 }}>
                      {React.cloneElement(config.icon, { fontSize: "small" })}
                    </Box>
                    <Typography variant="body2" sx={{ color: "white" }}>
                      {config.title}
                    </Typography>
                    {cardVisibility[key] && (
                      <Box sx={{ ml: "auto" }}>
                        <VisibilityIcon sx={{ fontSize: 16, color: "white" }} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Alerts */}
      {cardVisibility.alerts && alerts.length > 0 && (
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {alerts.map((alert, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Alert severity={alert.type} sx={{ borderRadius: 2 }}>
                {alert.text}
              </Alert>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Cards Grid */}
      <Grid container spacing={1}>
        {cardVisibility.contact && (
          <Grid item xs={12} md={cardConfig.contact.size}>
            <ContactCard />
          </Grid>
        )}

        {cardVisibility.medical && (
          <Grid item xs={12} md={cardConfig.medical.size}>
            <MedicalCard />
          </Grid>
        )}

        {cardVisibility.insurance && (
          <Grid item xs={12} md={cardConfig.insurance.size}>
            <InsuranceCard />
          </Grid>
        )}

        {cardVisibility.provider && (
          <Grid item xs={12} md={cardConfig.provider.size}>
            <ProviderCard />
          </Grid>
        )}

        {cardVisibility.authorizations && (
          <Grid item xs={12} md={cardConfig.authorizations.size}>
            <AuthorizationsCard />
          </Grid>
        )}
        {cardVisibility.documents && (
          <Grid item xs={12} md={cardConfig.documents.size}>
            <ClaimCard />
          </Grid>
        )}
        {cardVisibility.appointments && (
          <Grid item xs={12} md={cardConfig.appointments.size}>
            <AppointmentsCard />
          </Grid>
        )}
        {cardVisibility.financial && (
          <Grid item xs={12} md={cardConfig.financial.size}>
            <FinancialCard />
          </Grid>
        )}
        {cardVisibility.Comments && (
          <Grid item xs={12} md={cardConfig.financial.size}>
            {/* <PatientCommentsCard /> */}
            <Card
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "error.light",
                background:
                  "linear-gradient(145deg, rgba(255,235,238,0.9), rgba(255,255,255,1))",
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                "&:hover": {
                  boxShadow: "0 12px 32px rgba(0,0,0,0.12)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Grid container spacing={2}>
                  {/* Header */}
                  <Grid
                    item
                    xs={12}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          bgcolor: "error.main",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Comment sx={{ color: "#fff", fontSize: 18 }} />
                      </Box>

                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        letterSpacing={0.3}
                      >
                        Comments/Notes
                      </Typography>
                    </Box>

                    {/* Edit / Save Button */}
                    {isEditing ? (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={handleSaveComments}
                      >
                        Save
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={handleEdit}
                      >
                        Edit
                      </Button>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        position: "relative",
                        bgcolor: "#fff",
                        borderRadius: 3,
                        p: 2,
                        border: "1px dashed",
                        borderColor: "error.light",
                      }}
                    >
                      {/* Accent bar */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: 6,
                          borderRadius: "3px 0 0 3px",
                          bgcolor: "error.main",
                        }}
                      />

                      {isEditing ? (
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          sx={{
                            pl: 1,
                            "& .MuiOutlinedInput-root": { border: "none" },
                          }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
    pl: 2,
    // color: "error.dark",
    fontWeight: 500,
    lineHeight: 1.6,
    whiteSpace: "pre-wrap", // preserves \n line breaks
    wordWrap: "break-word", // wraps long words
  }}
                        >
                          {commentText ||
                            "No comments available for this patient."}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

export default PatientSummary;
